/**
 * The chart of accounts' vocabulary as the screens need it (ADR-0066): the classes in statement order,
 * the account types within each, and the statement lines each class can sit on.
 *
 * The statement lines are read from `localization/coa/statement-lines.json`, the file the database's
 * `ref.statement_line` is seeded from, so a dialog offers exactly the lines the server accepts.
 */
import vocabulary from "../../../localization/coa/statement-lines.json";
import type { Account, AccountGroup, AccountSubtype, CoaTemplate } from "@/api/types";

/** The tooltip on a required account's badge and lock (handoff §4 rule 9); the reason follows it. */
export const REQUIRED_TIP = "Required account — it can't be edited or removed.";

/** Why Add suggested accounts is off once anything has posted (handoff §6.7). */
export const SUGGESTED_LOCKED =
  "Suggested accounts can only be added before the first transaction posts. Add accounts one at a time with New account.";

/** The classes, in the order a statement and the chart list them. */
export const CLASS_ORDER: AccountGroup[] = [
  "Assets",
  "Liabilities",
  "Equity",
  "Income",
  "Expenses",
];

/** "Asset" in the vocabulary file is "Assets" on the wire. */
const CLASS_OF: Record<string, AccountGroup> = {
  Asset: "Assets",
  Liability: "Liabilities",
  Equity: "Equity",
  Income: "Income",
  Expense: "Expenses",
};

export interface StatementLine {
  code: string;
  name: string;
  statement: "financial_position" | "income_statement" | string;
  /** The classes an account on this line can be. */
  groups: AccountGroup[];
}

export const STATEMENT_LINES: StatementLine[] = vocabulary.lines
  .toSorted((a, b) => a.ordinal - b.ordinal)
  .map((line) => ({
    code: line.code,
    name: line.name,
    statement: line.statement,
    groups: line.classes.map((c) => CLASS_OF[c]!),
  }));

/** The line's name, or "Not in layout" for an account on none. */
export function statementLineName(code: string | null | undefined): string {
  if (!code) return "Not in layout";
  return STATEMENT_LINES.find((line) => line.code === code)?.name ?? code;
}

export const statementLinesFor = (group: AccountGroup) =>
  STATEMENT_LINES.filter((line) => line.groups.includes(group));

/** The account types within each class (handoff §7). */
export const SUBTYPES: Array<{ value: AccountSubtype; label: string; group: AccountGroup }> = [
  { value: "current_asset", label: "Current asset", group: "Assets" },
  { value: "non_current_asset", label: "Non-current asset", group: "Assets" },
  { value: "current_liability", label: "Current liability", group: "Liabilities" },
  { value: "non_current_liability", label: "Non-current liability", group: "Liabilities" },
  { value: "contributed_equity", label: "Contributed equity", group: "Equity" },
  { value: "retained_earnings", label: "Retained earnings", group: "Equity" },
  { value: "reserves", label: "Reserves", group: "Equity" },
  { value: "other_equity", label: "Other equity", group: "Equity" },
  { value: "revenue", label: "Revenue", group: "Income" },
  { value: "other_income", label: "Other income", group: "Income" },
  { value: "finance_income", label: "Finance income", group: "Income" },
  { value: "cost_of_sales", label: "Cost of sales", group: "Expenses" },
  { value: "operating_expense", label: "Operating expense", group: "Expenses" },
  { value: "finance_cost", label: "Finance cost", group: "Expenses" },
  { value: "tax_expense", label: "Tax expense", group: "Expenses" },
  { value: "other_expense", label: "Other expense", group: "Expenses" },
];

export const subtypesFor = (group: AccountGroup) => SUBTYPES.filter((s) => s.group === group);

export function subtypeLabel(subtype: AccountSubtype | null | undefined): string | null {
  return SUBTYPES.find((s) => s.value === subtype)?.label ?? null;
}

/** Codes 1000–1099 are Banking's: a template never touches them, and New account can't use them. */
export const isBankCode = (code: string) => /^10\d{2}$/.test(code.trim());

/** The template code an account came from, from its source ("template:NFRS-SME@2026.11"). */
export function templateOf(account: Pick<Account, "source">): string | null {
  const match = /^template:([^@]+)@/.exec(account.source ?? "");
  return match ? match[1]! : null;
}

/** Whether the company made this account itself, rather than a template, Banking or the seed. */
export const isCompanyMade = (account: Pick<Account, "source">) =>
  (account.source ?? "tenant") === "tenant";

/** The region a template is offered for, as a person reads it. */
export function regionName(region: string | null): string {
  if (region === "NP") return "Nepal";
  return region ?? "Any country";
}

/** NFRS for SMEs for a Nepal company, IFRS for SMEs (or whatever is not Nepal's) otherwise. */
export function templateFor(templates: CoaTemplate[], country: string | null | undefined) {
  if (country === "NP") {
    return templates.find((t) => t.region === "NP") ?? templates.find((t) => t.isDefault);
  }
  return templates.find((t) => t.region !== "NP") ?? templates[0];
}

/** The first sentence of a template's description, for its card. */
export const firstSentence = (text: string) => /^.*?[.!?](\s|$)/.exec(text)?.[0].trim() ?? text;

/** Accounts in class order, then code order. */
export function byClass<T extends { group: AccountGroup; code: string }>(
  accounts: readonly T[],
): Array<{ group: AccountGroup; accounts: T[] }> {
  return CLASS_ORDER.map((group) => ({
    group,
    accounts: accounts
      .filter((a) => a.group === group)
      .toSorted((a, b) => a.code.localeCompare(b.code)),
  })).filter((section) => section.accounts.length > 0);
}

const REFERENCE_KINDS: Record<string, string> = {
  posted: "Posted ledger lines",
  journal: "Journals",
  invoice: "Invoices",
  quotation: "Quotations",
  receipt: "Receipts",
  billing_item: "Billing items",
  bill: "Bills",
  purchase_order: "Purchase orders",
  payment: "Payments",
  employee_movement: "Employee ledger entries",
  bank_account: "Bank accounts",
  fund_transfer: "Fund transfers",
  report_mapping: "Report mapping",
  year_end: "Year-end settings",
  tax_code: "Tax codes",
  approval_rule: "Approval rules",
};

/** What holds on to an account, as a person reads it. */
export const referenceKindName = (kind: string) =>
  REFERENCE_KINDS[kind] ?? kind.replaceAll("_", " ");
